import * as React from 'react';
import { Button, ButtonTheme } from '@cian/button-component';
import { IAgentSchema, IOfferSchema, IUserSchema } from '@cian/api-models/offer-card/v1/get-offer-data';
import { Avatar } from '../../common/components/avatar';
import { IOfferChatsState } from '../../store/offer_chats';
import { getFormattedPhoneNumber } from '../../utils/formatters';
import { SimpleBotDetector } from '../../utils/simpleBotDetector';
import { createAgentIdName } from '../../utils/createAgentIdName';
import { getAgentAvatar } from '../../utils/getAgentAvatar';
import { PopupOwnOfferContainer } from '../popups/own_offer/container';
import { PopupStartChatContainer } from '../popups/start_chat/container';
import {
  trackFirstMessageShowPopup,
  trackFirstMessageClick,
  trackGoToMessages,
  trackClickShowPhone,
  trackFirstMessageSent,
} from './HomeownerBlock.tracking';
import * as styles from './HomeownerBlock.css';
import { ABCallButton } from '../ab_call_button';
import { GreenTick } from '../Icons';
import { HOMEOWNER_PROOF_TEXTS } from '../../constants/homeownerProofTexts';
import { CalltrackingBadgeContainer } from '../../containers/CalltrackingBadge';
import { CallSourceWishContainer } from '../../containers/CallSourceWish';

export interface IHomeownerBlockProps {
  abGroup: number;
  agent: IAgentSchema | null;
  offer: IOfferSchema;
  user: IUserSchema | null;
  offerChat: IOfferChatsState;
  isAuthenticated: boolean;
  isOfferOwnedByUser: boolean;
  isAgentAvailable: boolean;
  isDealRentCianAgencyEnabled: boolean;

  chatMessageSent(): void;
  setPhoneCollapse(): void;
}

export interface IHomeownerBlockState {
  isPhonesVisible: boolean;
  isStartChatPopupOpen: boolean;
  isOwnOfferPopupOpen: boolean;
  startChatMessage: string;
}

export class HomeownerBlock extends React.PureComponent<IHomeownerBlockProps, IHomeownerBlockState> {
  public state = {
    isOwnOfferPopupOpen: false,
    isPhonesVisible: false,
    isStartChatPopupOpen: false,
    startChatMessage: '',
  };

  private botDetector = new SimpleBotDetector();

  public componentDidMount() {
    this.botDetector.init();
  }

  public render() {
    const { agent, offer, offerChat, isDealRentCianAgencyEnabled } = this.props;
    const { phones, homeownerProofs } = offer;
    const { isChatsEnabled } = offerChat;
    const { isStartChatPopupOpen, startChatMessage, isOwnOfferPopupOpen } = this.state;

    if (!agent || isDealRentCianAgencyEnabled) {
      return null;
    }

    const userIdName = createAgentIdName(agent);
    const avatar = getAgentAvatar(agent);

    return (
      <section className={styles['container']} id="author">
        <Avatar url={avatar} className={styles['image']} isRoundness />

        <h2 className={styles['title']}>Собственник</h2>

        <div className={styles['id']}>{userIdName}</div>

        {Array.isArray(homeownerProofs) && homeownerProofs.length > 0 && (
          <ul className={styles['proofs']}>
            {homeownerProofs.map(proof => (
              <li className={styles['proof']} key={proof}>
                <GreenTick />
                {HOMEOWNER_PROOF_TEXTS[proof]}
              </li>
            ))}
          </ul>
        )}

        {(phones || isChatsEnabled) && (
          <div className={styles['buttons']}>
            {phones && !phones.some(phone => !phone.countryCode || !phone.number) && this.state.isPhonesVisible
              ? this.renderPhones()
              : this.renderShowPhoneButton()}

            {isChatsEnabled && this.renderSendMessageButton()}
          </div>
        )}

        {this.state.isPhonesVisible && <CalltrackingBadgeContainer />}
        {this.state.isPhonesVisible && (
          <div className={styles['wish']}>
            <CallSourceWishContainer />
          </div>
        )}

        <PopupStartChatContainer
          isOpen={isStartChatPopupOpen}
          message={startChatMessage}
          onMessageChange={this.handleMessageChange}
          onMessageSent={this.handleMessageSent}
          onClose={this.handlePopupStartChatClose}
        />
        <PopupOwnOfferContainer isOpen={isOwnOfferPopupOpen} onClose={this.handlePopupOwnOfferClose} />
      </section>
    );
  }

  private renderShowPhoneButton = () => {
    const { offer } = this.props;

    return offer.status === 'published' ? (
      <Button onClick={this.handleShowPhoneClick} className={styles['button']}>
        <ABCallButton />
      </Button>
    ) : null;
  };

  private renderSendMessageButton = () => {
    const {
      isOfferOwnedByUser,
      offerChat: { chatId, isChatMessageSent },
    } = this.props;

    return (
      <Button theme={ButtonTheme.Ghost} onClick={this.handleStartChatButtonClick}>
        {(!!chatId || isChatMessageSent) && !isOfferOwnedByUser ? 'Посмотреть сообщения' : 'Написать сообщение'}
      </Button>
    );
  };

  private renderPhones = () => {
    const { phones } = this.props.offer;

    if (!phones || phones.some(phone => !phone.countryCode || !phone.number)) {
      return null;
    }

    return <span className={styles['phones']}>{phones.map(getFormattedPhoneNumber).join(', ')}</span>;
  };

  private handleStartChatButtonClick = () => {
    const {
      isAgentAvailable,
      offer,
      agent,
      isAuthenticated,
      isOfferOwnedByUser,
      offerChat: { chatId, isChatMessageSent },
    } = this.props;

    trackFirstMessageClick(offer, agent);

    switch (true) {
      case !isAuthenticated:
        /* istanbul ignore next */
        if (window.waitForLoginPopup) {
          window.waitForLoginPopup('chats');
        }

        break;

      case Boolean(isChatMessageSent || !!chatId):
        trackGoToMessages();

        window.location.href = `/dialogs/?offerId=${offer.id}`;

        break;

      case isOfferOwnedByUser:
        this.setState({ isOwnOfferPopupOpen: true });

        break;

      default:
        this.setState({ isStartChatPopupOpen: true });

        trackFirstMessageShowPopup(offer, agent, isAgentAvailable ? undefined : 'open_message');
    }
  };

  private openChatFromPhones = () => {
    const {
      offer,
      isAuthenticated,
      agent,
      isAgentAvailable,
      isOfferOwnedByUser,
      offerChat: { isChatsEnabled },
    } = this.props;

    if (isChatsEnabled && !isAgentAvailable) {
      if (isOfferOwnedByUser) {
        return this.setState({ isOwnOfferPopupOpen: true });
      }

      if (isAuthenticated) {
        this.setState({ isStartChatPopupOpen: true });

        trackFirstMessageShowPopup(offer, agent, 'open_phone');
      } else {
        if (window.waitForLoginPopup) {
          window.waitForLoginPopup();
        }
      }
    }
  };

  private handleShowPhoneClick = () => {
    const { abGroup, user, offer, agent, setPhoneCollapse } = this.props;

    trackClickShowPhone(offer, agent, abGroup, user, !this.botDetector.isHuman());

    setPhoneCollapse();

    this.setState({ isPhonesVisible: true });

    this.openChatFromPhones();
  };

  private handleMessageSent = () => {
    const { agent, offer, user, chatMessageSent } = this.props;

    chatMessageSent();

    trackFirstMessageSent(agent, offer, user, null);
  };

  private handleMessageChange = (message: string) => this.setState({ startChatMessage: message });
  private handlePopupStartChatClose = () => this.setState({ isStartChatPopupOpen: false });
  private handlePopupOwnOfferClose = () => this.setState({ isOwnOfferPopupOpen: false });
}
